import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, ArrowRight } from "lucide-react";
import SEOHead from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";
import AdSensePlaceholder from "../components/AdSensePlaceholder";
import ArticleCard from "../components/ArticleCard";
import FAQAccordion from "../components/FAQAccordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  administratif: "Administratif",
  finance: "Finance",
  sante: "Santé",
  auto: "Auto & Transport",
  famille: "Famille",
  "vie-pratique": "Vie Pratique",
};

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1615749356515-6b07ae5d5378?w=400&q=70";

export default function PillarPage() {
  const { slug } = useParams();
  const [pillar, setPillar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/pillars/${slug}`)
      .then((res) => setPillar(res.data))
      .catch(() => setError("Page pilier introuvable."))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-2/3" />
          <div className="h-4 bg-slate-200 rounded w-full" />
          <div className="h-4 bg-slate-200 rounded w-4/5" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-200 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="pillar-error"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center"
      >
        <p className="text-slate-500 text-lg">{error}</p>
        <Link to="/" className="mt-4 inline-block text-blue-600 hover:underline">
          Retour à l'accueil
        </Link>
      </div>
    );
  }

  // Build a lookup map of articles by slug
  const articlesBySlug = {};
  (pillar.linkedArticlesData || []).forEach((a) => {
    articlesBySlug[a.slug] = a;
  });

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: pillar.seoTitle,
      description: pillar.seoDescription,
      url: `https://marocpratique.com/pilier/${slug}`,
      publisher: {
        "@type": "Organization",
        name: "MarocPratique",
        url: "https://marocpratique.com",
      },
    },
    // BreadcrumbList
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://marocpratique.com" },
        { "@type": "ListItem", position: 2, name: pillar.title, item: `https://marocpratique.com/pilier/${slug}` },
      ],
    },
    // FAQPage si FAQ présente
    ...(pillar.faq && pillar.faq.length > 0
      ? [{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: pillar.faq.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: { "@type": "Answer", text: item.answer },
          })),
        }]
      : []),
  ];

  return (
    <>
      <SEOHead
        title={pillar.seoTitle}
        description={pillar.seoDescription}
        canonical={`https://marocpratique.com/pilier/${slug}`}
        type="article"
        jsonLd={jsonLd}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumbs
          items={[
            { label: "Guides Piliers", href: "/" },
            { label: pillar.title, href: `/pilier/${slug}` },
          ]}
        />

        {/* Pillar Hero */}
        <header
          data-testid="pillar-header"
          className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-6 sm:p-10 mb-10"
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-200">
              <BookOpen className="w-3.5 h-3.5" />
              Guide Complet
            </span>
          </div>
          <h1
            data-testid="pillar-title"
            className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight mb-4"
          >
            {pillar.title}
          </h1>
          <p
            data-testid="pillar-intro"
            className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-3xl"
          >
            {pillar.intro}
          </p>

          {/* Quick stats */}
          <div className="flex flex-wrap gap-4 mt-6 pt-6 border-t border-blue-200">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <BookOpen className="w-4 h-4 text-blue-500" />
              <span>
                <strong>{pillar.linkedArticlesData?.length || 0}</strong> articles inclus
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span>
                <strong>{pillar.sections?.length || 0}</strong> sections
              </span>
            </div>
          </div>
        </header>

        <AdSensePlaceholder position="banner" />

        {/* Sections */}
        {pillar.sections && pillar.sections.length > 0 && (
          <div data-testid="pillar-sections" className="space-y-10 mb-12">
            {pillar.sections.map((section, sIdx) => {
              const sectionArticles = (section.articles || [])
                .map((s) => articlesBySlug[s])
                .filter(Boolean);

              return (
                <section key={sIdx} data-testid={`pillar-section-${sIdx}`}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center font-heading font-bold text-sm flex-shrink-0">
                      {sIdx + 1}
                    </div>
                    <div>
                      <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900">
                        {section.title}
                      </h2>
                      {section.description && (
                        <p className="text-sm text-slate-500 mt-0.5">{section.description}</p>
                      )}
                    </div>
                  </div>

                  {sectionArticles.length > 0 ? (
                    <div className="space-y-3 ml-11">
                      {sectionArticles.map((article) => (
                        <Link
                          key={article.slug}
                          to={`/article/${article.slug}`}
                          data-testid={`pillar-article-link-${article.slug}`}
                          className="flex items-center gap-4 p-4 bg-white border border-slate-200 rounded-xl hover:border-blue-200 hover:shadow-md transition-all duration-200 group"
                        >
                          <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-slate-100">
                            <img
                              src={article.featured_image || FALLBACK_IMAGE}
                              alt={article.title}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
                              {CATEGORY_LABELS[article.category] || article.category}
                            </span>
                            <p className="text-slate-800 font-semibold text-sm sm:text-base leading-snug mt-0.5 group-hover:text-blue-700 transition-colors">
                              {article.title}
                            </p>
                            {article.excerpt && (
                              <p className="text-xs text-slate-500 mt-1 line-clamp-1">
                                {article.excerpt}
                              </p>
                            )}
                          </div>
                          <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 flex-shrink-0 transition-colors" />
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </section>
              );
            })}
          </div>
        )}

        {/* All Linked Articles grid */}
        {pillar.linkedArticlesData && pillar.linkedArticlesData.length > 0 && (
          <section data-testid="pillar-all-articles" className="mb-12">
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b border-slate-200">
              Tous les articles de ce guide
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pillar.linkedArticlesData.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </section>
        )}

        <AdSensePlaceholder position="rectangle" />

        {/* FAQ section */}
        {pillar.faq && pillar.faq.length > 0 && (
          <section data-testid="pillar-faq" className="mb-12">
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b border-slate-200">
              Questions fréquentes
            </h2>
            <FAQAccordion faq={pillar.faq} />
          </section>
        )}
      </div>
    </>
  );
}
